import actions from './actions';

const apiConfigModule = {
  state: () => ({
    // Список конфигураций
    apiConfigs: [],
    apiConfigsTotal: 0,
    isApiConfigsLoaded: false,

    // Текущая конфигурация для просмотра/редактирования
    currentApiConfig: null,
    isCurrentApiConfigLoaded: false,

    // Тестирование подключения
    testConnectionResult: null,
    isTestingConnection: false,

    // Предпросмотр данных
    previewData: null,
    isPreviewLoading: false,

    // Debug информация
    debugInfo: null,
    isDebugLoading: false,

    // Логи синхронизации
    syncLogs: [],
    syncLogsTotal: 0,
    isSyncLogsLoaded: false,

    // Последняя синхронизация
    lastSync: null,
    isLastSyncLoaded: false,

    // Статус текущей синхронизации
    syncStatus: null,
    isSyncing: false,

    // Интервал для автообновления статуса
    syncStatusInterval: null,
  }),

  getters: {
    sortedApiConfigs(state) {
      return state.apiConfigs || [];
    },

    activeApiConfigs(state) {
      return state.apiConfigs.filter((config) => config.isActive) || [];
    },

    hasActiveSync(state) {
      return state.apiConfigs.some((config) => config.lastSyncStatus === 'in_progress');
    },
  },

  mutations: {
    // Получение списка конфигураций
    GET_API_CONFIGS_REQUEST(state) {
      state.isApiConfigsLoaded = false;
    },

    GET_API_CONFIGS_SUCCESS(state, { data, total }) {
      state.apiConfigs = data;
      state.apiConfigsTotal = Number(total) || 0;
      state.isApiConfigsLoaded = true;
    },

    GET_API_CONFIGS_ERROR(state) {
      state.isApiConfigsLoaded = true;
      state.apiConfigs = [];
      state.apiConfigsTotal = 0;
    },

    // Получение одной конфигурации
    GET_API_CONFIG_REQUEST(state) {
      state.isCurrentApiConfigLoaded = false;
    },

    GET_API_CONFIG_SUCCESS(state, config) {
      state.currentApiConfig = config;
      state.isCurrentApiConfigLoaded = true;
    },

    GET_API_CONFIG_ERROR(state) {
      state.isCurrentApiConfigLoaded = true;
      state.currentApiConfig = null;
    },

    // Создание конфигурации
    CREATE_API_CONFIG_REQUEST() {
      // Loading state handled by component
    },

    CREATE_API_CONFIG_SUCCESS(state, config) {
      state.apiConfigs.unshift(config);
      state.apiConfigsTotal += 1;
    },

    CREATE_API_CONFIG_ERROR() {
      // Error handled by action
    },

    // Обновление конфигурации
    UPDATE_API_CONFIG_REQUEST() {
      // Loading state handled by component
    },

    UPDATE_API_CONFIG_SUCCESS(state, updatedConfig) {
      const index = state.apiConfigs.findIndex((c) => c.id === updatedConfig.id);
      if (index !== -1) {
        state.apiConfigs.splice(index, 1, updatedConfig);
      }
    },

    UPDATE_API_CONFIG_ERROR() {
      // Error handled by action
    },

    // Удаление конфигурации
    DELETE_API_CONFIG_REQUEST() {
      // Loading state handled by component
    },

    DELETE_API_CONFIG_SUCCESS(state, id) {
      state.apiConfigs = state.apiConfigs.filter((c) => c.id !== id);
      state.apiConfigsTotal -= 1;
    },

    DELETE_API_CONFIG_ERROR() {
      // Error handled by action
    },

    // Тестирование подключения
    TEST_CONNECTION_REQUEST(state) {
      state.isTestingConnection = true;
      state.testConnectionResult = null;
    },

    TEST_CONNECTION_SUCCESS(state, result) {
      state.testConnectionResult = result;
      state.isTestingConnection = false;
    },

    TEST_CONNECTION_ERROR(state, error) {
      state.testConnectionResult = { success: false, error };
      state.isTestingConnection = false;
    },

    CLEAR_TEST_RESULT(state) {
      state.testConnectionResult = null;
    },

    // Предпросмотр данных
    GET_PREVIEW_REQUEST(state) {
      state.isPreviewLoading = true;
      state.previewData = null;
    },

    GET_PREVIEW_SUCCESS(state, data) {
      state.previewData = data;
      state.isPreviewLoading = false;
    },

    GET_PREVIEW_ERROR(state) {
      state.isPreviewLoading = false;
      state.previewData = null;
    },

    CLEAR_PREVIEW(state) {
      state.previewData = null;
    },

    // Debug информация
    GET_DEBUG_REQUEST(state) {
      state.isDebugLoading = true;
      state.debugInfo = null;
    },

    GET_DEBUG_SUCCESS(state, data) {
      state.debugInfo = data;
      state.isDebugLoading = false;
    },

    GET_DEBUG_ERROR(state) {
      state.isDebugLoading = false;
      state.debugInfo = null;
    },

    CLEAR_DEBUG(state) {
      state.debugInfo = null;
    },

    // Логи синхронизации
    GET_SYNC_LOGS_REQUEST(state) {
      state.isSyncLogsLoaded = false;
    },

    GET_SYNC_LOGS_SUCCESS(state, { data, total }) {
      state.syncLogs = data;
      state.syncLogsTotal = Number(total) || 0;
      state.isSyncLogsLoaded = true;
    },

    GET_SYNC_LOGS_ERROR(state) {
      state.isSyncLogsLoaded = true;
      state.syncLogs = [];
      state.syncLogsTotal = 0;
    },

    // Последняя синхронизация
    GET_LAST_SYNC_REQUEST(state) {
      state.isLastSyncLoaded = false;
    },

    GET_LAST_SYNC_SUCCESS(state, data) {
      state.lastSync = data;
      state.isLastSyncLoaded = true;
    },

    GET_LAST_SYNC_ERROR(state) {
      state.isLastSyncLoaded = true;
      state.lastSync = null;
    },

    // Запуск синхронизации
    START_SYNC_REQUEST(state) {
      state.isSyncing = true;
      state.syncStatus = { status: 'starting' };
    },

    START_SYNC_SUCCESS(state, result) {
      state.isSyncing = false;
      state.syncStatus = result;
    },

    START_SYNC_ERROR(state, error) {
      state.isSyncing = false;
      state.syncStatus = { status: 'error', error };
    },

    // Обновление статуса синхронизации
    UPDATE_SYNC_STATUS(state, { configId, status }) {
      const config = state.apiConfigs.find((c) => c.id === configId);
      if (config) {
        config.lastSyncStatus = status.status;
        if (status.lastSyncAt) {
          config.lastSyncAt = status.lastSyncAt;
        }
        if (status.lastSyncError) {
          config.lastSyncError = status.lastSyncError;
        }
        if (status.lastSyncUsersCount !== undefined) {
          config.lastSyncUsersCount = status.lastSyncUsersCount;
        }
      }
    },

    // Установка интервала автообновления
    SET_SYNC_STATUS_INTERVAL(state, interval) {
      state.syncStatusInterval = interval;
    },

    CLEAR_SYNC_STATUS_INTERVAL(state) {
      if (state.syncStatusInterval) {
        clearInterval(state.syncStatusInterval);
        state.syncStatusInterval = null;
      }
    },
  },

  actions,
  namespaced: true,
};

export default apiConfigModule;
